import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { onlineManager, useMutation } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { createAuthStore, identityKey, onIdentityChange, useAuth } from '@/shared/auth';
import type { InvitationLinkResponse } from '@/shared/api';

export function useInvitationLink(targetId: string, open: boolean) {
  const auth = useAuth();
  const identity = [
    identityKey(auth.user),
    auth.user?.homeTenantId,
    auth.user?.sessionFamilyId,
    auth.user?.roles.slice().sort().join(','),
    auth.status === 'unauthenticated',
  ].join('|');
  const binding = useMemo(
    () => ({ api: auth.api, identity, targetId, open }),
    [auth.api, identity, targetId, open],
  );
  const active = useRef<typeof binding | null>(binding);
  const attempt = useRef(0);
  const inFlight = useRef(false);
  const [sending, setSending] = useState(false);
  const [secret, setSecret] = useState<{
    binding: typeof binding;
    value: InvitationLinkResponse;
  } | null>(null);
  const [notice, setNotice] = useState<'failed' | 'unknown' | 'copied' | 'copyFailed' | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [now, setNow] = useState(Date.now());
  const online = useSyncExternalStore(
    (listener) => onlineManager.subscribe(listener),
    () => onlineManager.isOnline(),
  );
  const clear = useCallback(() => {
    inFlight.current = false;
    setSending(false);
    attempt.current++;
    setSecret(null);
    setNotice(null);
    setAttempted(false);
  }, []);
  const current = (scope: typeof binding, request: number) => {
    const live = createAuthStore().getState();
    return (
      active.current === scope &&
      scope.open &&
      attempt.current === request &&
      live.api === scope.api &&
      live.user?.roles.includes('WorkspaceAdmin') &&
      [
        identityKey(live.user),
        live.user?.homeTenantId,
        live.user?.sessionFamilyId,
        live.user?.roles.slice().sort().join(','),
        live.status === 'unauthenticated',
      ].join('|') === scope.identity
    );
  };
  const mutation = useMutation({
    retry: false,
    networkMode: 'always',
    gcTime: 0,
    mutationFn: async (request: number): Promise<void> => {
      const scope = binding;
      try {
        if (!onlineManager.isOnline() || !current(scope, request)) return;
        setSecret(null);
        setNotice(null);
        setAttempted(true);
        const value = await scope.api.users.generateInvitationLink(scope.targetId);
        if (!value.url || !Number.isFinite(Date.parse(value.expiresAt)))
          throw new TypeError('Invalid invitation response');
        if (current(scope, request)) {
          setNow(Date.now());
          setSecret({ binding: scope, value });
        }
      } catch (error) {
        if (current(scope, request)) {
          const refused =
            error instanceof HTTPError &&
            error.response.status >= 400 &&
            error.response.status < 500;
          setNotice(refused ? 'failed' : 'unknown');
        }
      } finally {
        if (active.current === scope && attempt.current === request) {
          inFlight.current = false;
          setSending(false);
        }
      }
    },
  });
  const reset = mutation.reset;
  useLayoutEffect(() => {
    active.current = binding;
    clear();
    reset();
    return () => {
      active.current = null;
    };
  }, [binding, reset, clear]);
  useEffect(() => onIdentityChange(clear), [clear]);
  useEffect(() => {
    if (!secret) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [secret]);
  const value = secret?.binding === binding ? secret.value : null;
  const expired = value !== null && Date.parse(value.expiresAt) <= now;
  const copy = async () => {
    const request = attempt.current;
    if (!value || Date.parse(value.expiresAt) <= Date.now() || !current(binding, request)) return;
    try {
      await navigator.clipboard.writeText(value.url);
      if (current(binding, request)) setNotice('copied');
    } catch {
      if (current(binding, request)) setNotice('copyFailed');
    }
  };
  const generate = () => {
    if (inFlight.current || !onlineManager.isOnline() || !current(binding, attempt.current)) return;
    inFlight.current = true;
    attempt.current++;
    setSecret(null);
    setNotice(null);
    setAttempted(true);
    setSending(true);
    mutation.mutate(attempt.current);
  };
  return { value, expired, notice, attempted, online, pending: sending, generate, copy, clear };
}
